/**
 * Single source of truth for vital-sign definitions (NOM-004).
 *
 * Both the capture form (ConsultationForm) and the read-only trend chart
 * (VitalsTrendChart) derive their fields from this list, so the set of vitals
 * the clinician records can never drift from the set that is trended
 * (REQ-FE-02 / REQ-FE-06). Vitals are stored as structured numeric values
 * keyed by `key`; nothing derived is persisted.
 */
export interface VitalDef {
  key: string
  /** Spanish clinical label. */
  label: string
  unit: string
  /** If true, a rising value is clinically adverse (drives delta colouring). */
  higherIsWorse?: boolean
}

export const VITALS: readonly VitalDef[] = [
  { key: 'bp_systolic', label: 'Presión sistólica', unit: 'mmHg', higherIsWorse: true },
  { key: 'bp_diastolic', label: 'Presión diastólica', unit: 'mmHg', higherIsWorse: true },
  { key: 'heart_rate', label: 'Frecuencia cardíaca', unit: 'lpm' },
  { key: 'resp_rate', label: 'Frecuencia respiratoria', unit: 'rpm' },
  { key: 'temperature', label: 'Temperatura', unit: '°C', higherIsWorse: true },
  { key: 'spo2', label: 'SpO2', unit: '%' },
  { key: 'weight', label: 'Peso', unit: 'kg' },
  { key: 'height', label: 'Estatura', unit: 'cm' },
  { key: 'glucose', label: 'Glucosa', unit: 'mg/dL', higherIsWorse: true },
] as const

export const VITALS_BY_KEY: Record<string, VitalDef> = Object.fromEntries(
  VITALS.map((v) => [v.key, v]),
)

export const vitalLabel = (key: string): string => VITALS_BY_KEY[key]?.label ?? key
export const vitalUnit = (key: string): string => VITALS_BY_KEY[key]?.unit ?? ''

/**
 * Coerce a raw stored vital to a finite number, or null when the value is
 * absent or non-numeric. Used to skip gaps without breaking a trend.
 */
export function toNumericVital(raw: unknown): number | null {
  if (raw == null || raw === '') return null
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(n) ? n : null
}
